import { useCallback, useEffect, useState } from "react";
import { Download, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, downloadFile, money, shortDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusPill } from "@/components/StatusPill";

export function InvoicesPanel({ caseId, isStaff = false }) {
  const [invoices, setInvoices] = useState([]);
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");

  const load = useCallback(() => {
    api.get("/invoices", { params: { case_id: caseId } }).then(({ data }) => setInvoices(data)).catch(() => {});
  }, [caseId]);

  useEffect(load, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!amount) return;
    try {
      await api.post("/invoices", { case_id: caseId, amount: parseFloat(amount), description });
      setAmount("");
      setDescription("");
      toast.success("Invoice raised");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const setStatus = async (inv, status) => {
    try {
      await api.patch(`/invoices/${inv.id}`, null, { params: { status } });
      toast.success(`${inv.number} marked ${status}`);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="invoices-panel" className="space-y-6">
      {isStaff ? (
        <form onSubmit={create} className="glass rounded-xl p-4 flex flex-col sm:flex-row gap-3" data-testid="invoice-form">
          <Input
            data-testid="invoice-amount-input"
            type="number"
            step="0.01"
            placeholder="Amount (GBP)"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="bg-white/5 border-white/15 sm:w-44"
          />
          <Input
            data-testid="invoice-description-input"
            placeholder="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="bg-white/5 border-white/15"
          />
          <Button data-testid="invoice-create-btn" className="rounded-full bg-gold text-navy hover:bg-gold-dark shrink-0">
            <Plus className="h-4 w-4 mr-1.5" /> Raise invoice
          </Button>
        </form>
      ) : null}

      <div className="space-y-3">
        {invoices.map((inv) => (
          <div key={inv.id} className="glass rounded-xl px-5 py-4 flex flex-wrap items-center gap-4" data-testid="invoice-row">
            <div className="min-w-[190px] flex-1">
              <p className="text-sm text-[#F3F4F6] font-medium">{inv.number}</p>
              <p className="text-xs text-[#6B7280]">
                {inv.description} · issued {shortDate(inv.issued_at)} · due {shortDate(inv.due_date)}
              </p>
            </div>
            <p className="font-display text-lg text-gold">{money(inv.amount, inv.currency)}</p>
            <StatusPill value={inv.status} testId="invoice-status-pill" />
            {isStaff ? (
              <select
                data-testid="invoice-status-select"
                value={inv.status}
                onChange={(e) => setStatus(inv, e.target.value)}
                className="rounded-full bg-white/5 border border-white/15 px-3 py-1.5 text-xs text-[#F3F4F6]"
              >
                <option value="unpaid">unpaid</option>
                <option value="paid">paid</option>
                <option value="overdue">overdue</option>
              </select>
            ) : null}
            <Button
              data-testid="invoice-download-btn"
              size="sm"
              variant="ghost"
              className="rounded-full text-[#D1D5DB] hover:bg-white/10 hover:text-gold"
              onClick={() => downloadFile(`/invoices/${inv.id}/pdf`, `${inv.number}.pdf`).catch(() => toast.error("Download failed"))}
            >
              <Download className="h-4 w-4 mr-1.5" /> PDF
            </Button>
          </div>
        ))}
        {!invoices.length ? <p className="text-sm text-[#9CA3AF]">No invoices raised for this case.</p> : null}
      </div>
    </div>
  );
}
